import { createRouter, createWebHistory } from "vue-router";
import { resolveRouteAccess } from "./routerAccess.mjs";

const router = createRouter({
  history: createWebHistory("/user/"),
  scrollBehavior() {
    return { left: 0, top: 0 };
  },
  routes: [
    {
      path: "/",
      redirect: { name: "login", params: { userType: "customer" } }
    },
    {
      path: "/customer",
      meta: {
        authRequired: true,
        isCustomer: true
      },
      component: () => import("./layouts/main/customer.vue"),
      children: [
        {
          // =============================================================================
          // MAIN LAYOUT ROUTES
          // =============================================================================
          path: "",
          component: () => import("./layouts/main/Main.vue"),
          children: [
            // =============================================================================
            // Theme Routes
            // =============================================================================
            {
              path: "",
              redirect: { name: "publications", params: { type: "paper" } }
            },
            {
              path: "publications",
              redirect: { name: "publications", params: { type: "paper" } }
            },
            {
              path: "publications/:type",
              name: "publications",
              component: () => import("./views/eCommerce/ECommerceShop.vue"),
              meta: {
                breadcrumb: [
                  {
                    title: "breadcrumb.publications",
                    url: { name: "publications", params: { type: "paper" } }
                  }
                ],
                pageTitle: "breadcrumb.publications"
              }
            },
            {
              path: "publications/:type/:by/:item",
              name: "publicationsBy",
              component: () => import("./views/eCommerce/ECommerceShop.vue"),
              meta: {
                breadcrumb: [
                  {
                    title: "breadcrumb.publications",
                    url: { name: "publications", params: { type: "paper" } }
                  },
                  { title: "breadcrumb.publications", active: true }
                ],
                pageTitle: "breadcrumb.publications"
              }
            },
            {
              path: "publication/:id",
              name: "publication",
              component: () =>
                import("./views/eCommerce/ECommerceItemDetailView.vue"),
              meta: {
                breadcrumb: [
                  {
                    title: "breadcrumb.publications",
                    url: { name: "publications", params: { type: "paper" } }
                  },
                  {
                    title: "breadcrumb.publications",
                    url: { name: "publications", params: { type: "paper" } }
                  },
                  { title: "breadcrumb.publicationDetail", active: true }
                ],
                pageTitle: "breadcrumb.detail"
              }
            },
            {
              path: "checkout/:page?",
              name: "checkout",
              component: () =>
                import("./views/eCommerce/ECommerceCheckout.vue"),
              meta: {
                breadcrumb: [
                  {
                    title: "breadcrumb.publications",
                    url: { name: "publications", params: { type: "paper" } }
                  },
                  { title: "breadcrumb.checkout", active: true }
                ],
                pageTitle: "breadcrumb.checkout"
              }
            },
            {
              path: "search",
              name: "search",
              component: () => import("./views/searchResults.vue"),
              meta: {
                breadcrumb: [
                  {
                    title: "breadcrumb.publications",
                    url: { name: "publications", params: { type: "paper" } }
                  },
                  { title: "breadcrumb.search", active: true }
                ],
                pageTitle: "breadcrumb.search"
              }
            },
            {
              path: "wishlist",
              name: "wishlist",
              component: () =>
                import("./views/eCommerce/ECommerceWishList.vue"),
              meta: {
                breadcrumb: [
                  {
                    title: "breadcrumb.publications",
                    url: { name: "publications", params: { type: "paper" } }
                  },
                  { title: "breadcrumb.wishList", active: true }
                ],
                pageTitle: "breadcrumb.wishList"
              }
            },
            {
              path: "orders",
              name: "invoices",
              component: () => import("./views/orders/invoicesList.vue"),
              meta: {
                breadcrumb: [
                  {
                    title: "breadcrumb.publications",
                    url: { name: "publications", params: { type: "paper" } }
                  },
                  { title: "breadcrumb.ordersList", active: true }
                ],
                pageTitle: "breadcrumb.ordersList"
              }
            },
            {
              path: "order/:id",
              name: "invoiceDetails",
              component: () => import("./views/orders/invoiceDetails.vue"),
              meta: {
                breadcrumb: [
                  {
                    title: "breadcrumb.publications",
                    url: { name: "publications", params: { type: "paper" } }
                  },
                  { title: "breadcrumb.ordersList", url: { name: "invoices" } },
                  { title: "breadcrumb.orderDetails", active: true }
                ],
                pageTitle: "breadcrumb.orderDetails"
              }
            },
            {
              path: "claims",
              name: "claims",
              component: () =>
                import("./views/claims/claimsList/claimsList.vue"),
              meta: {
                breadcrumb: [
                  {
                    title: "breadcrumb.publications",
                    url: { name: "publications", params: { type: "paper" } }
                  },
                  { title: "breadcrumb.claims", active: true }
                ],
                pageTitle: "breadcrumb.claims"
              }
            },
            {
              path: "claim/:id",
              name: "claimDetails",
              component: () => import("./views/claims/claimDetail/Chat.vue"),
              meta: {
                breadcrumb: [
                  {
                    title: "breadcrumb.publications",
                    url: { name: "publications", params: { type: "paper" } }
                  },
                  { title: "breadcrumb.claims", url: { name: "claims" } },
                  { title: "breadcrumb.claimDetails", active: true }
                ],
                pageTitle: "breadcrumb.claimDetails"
              }
            },
            {
              path: "clients",
              name: "clients",
              component: () => import("./views/clients/clientsList.vue"),
              meta: {
                isSuperClient: true,
                breadcrumb: [
                  {
                    title: "breadcrumb.publications",
                    url: { name: "publications", params: { type: "paper" } }
                  },
                  { title: "breadcrumb.clientsList", active: true }
                ],
                pageTitle: "clientsList.title"
              }
            },
            {
              path: "client-action/:id?",
              name: "clientAction",
              component: () => import("./views/clientAction.vue"),
              meta: {
                isSuperClient: true,
                breadcrumb: [
                  {
                    title: "breadcrumb.publications",
                    url: { name: "publications", params: { type: "paper" } }
                  },
                  { title: "breadcrumb.clientsList", url: { name: "clients" } },
                  { title: "breadcrumb.clientAction", active: true }
                ],
                pageTitle: "breadcrumb.clientAction"
              }
            },
            {
              path: "product-request",
              name: "productRequest",
              component: () => import("./views/clients/productRequest.vue"),
              meta: {
                breadcrumb: [
                  {
                    title: "breadcrumb.publications",
                    url: { name: "publications", params: { type: "paper" } }
                  },
                  { title: "breadcrumb.productRequest", active: true }
                ],
                pageTitle: "breadcrumb.productRequest"
              }
            },
            {
              path: "exchange-rate",
              name: "exchangeRate",
              component: () => import("./views/clients/exchangeRate.vue"),
              meta: {
                breadcrumb: [
                  {
                    title: "breadcrumb.publications",
                    url: { name: "publications", params: { type: "paper" } }
                  },
                  { title: "breadcrumb.exchangeRate", active: true }
                ],
                pageTitle: "breadcrumb.exchangeRate"
              }
            },
            {
              path: "near-renewal",
              name: "nearRenewal",
              component: () => import("./views/orders/nearRenewal.vue"),
              meta: {
                breadcrumb: [
                  {
                    title: "breadcrumb.publications",
                    url: { name: "publications", params: { type: "paper" } }
                  }
                ],
                pageTitle: "breadcrumb.nearRenewal"
              }
            },
            {
              path: "cost-centers",
              name: "customerCostCenters",
              component: () => import("./views/costCenters.vue"),
              meta: {
                breadcrumb: [
                  {
                    title: "breadcrumb.publications",
                    url: { name: "publications", params: { type: "paper" } }
                  }
                ],
                pageTitle: "breadcrumb.costCenters"
              }
            },
            {
              path: "active-subscriptions",
              name: "activeSubscriptions",
              component: () =>
                import("./views/customerActiveSubscriptions.vue"),
              meta: {
                breadcrumb: [
                  {
                    title: "breadcrumb.publications",
                    url: { name: "publications", params: { type: "paper" } }
                  }
                ],
                pageTitle: "breadcrumb.activeSubscriptions"
              }
            },
            {
              path: "change-address-requests",
              name: "changeAddressReqs",
              component: () => import("./views/addressChangeRequests.vue"),
              meta: {
                breadcrumb: [
                  {
                    title: "breadcrumb.publications",
                    url: { name: "publications", params: { type: "paper" } }
                  }
                ],
                pageTitle: "breadcrumb.addressChangeRequests"
              }
            },
            {
              path: "list-hold-orders",
              name: "listHoldOrders",
              component: () => import("./views/listHoldOrders.vue"),
              meta: {
                breadcrumb: [
                  {
                    title: "breadcrumb.publications",
                    url: { name: "publications", params: { type: "paper" } }
                  }
                ],
                pageTitle: "breadcrumb.listHoldOrders"
              }
            },
            // {
            //   path: 'change-address-requests/:id/preview',
            //   name: 'changeAddressReqPreview',
            //   component: () => import('./views/addressChangeRequestPreview.vue'),
            // },
            {
              path: "superclient-orders",
              name: "superclientOrders",
              component: () => import("./views/superclientOrders.vue"),
              meta: {
                breadcrumb: [
                  {
                    title: "breadcrumb.publications",
                    url: { name: "publications", params: { type: "paper" } }
                  }
                ],
                pageTitle: "breadcrumb.superclientOrders"
              }
            },
            {
              path: "statistics",
              name: "statistics",
              component: () => import("./views/pages/statistics.vue"),
              meta: {
                breadcrumb: [
                  {
                    title: "breadcrumb.publications",
                    url: { name: "publications", params: { type: "paper" } }
                  },
                  { title: "breadcrumb.statistics", active: true }
                ],
                pageTitle: "breadcrumb.statistics"
              }
            },
            {
              path: "roles",
              name: "roles",
              component: () => import("./views/pages/roles.vue"),
              meta: {
                breadcrumb: [
                  {
                    title: "breadcrumb.publications",
                    url: { name: "publications", params: { type: "paper" } }
                  },
                  { title: "breadcrumb.clientsList", url: { name: "clients" } },
                  { title: "breadcrumb.roles", active: true }
                ],
                pageTitle: "breadcrumb.roles"
              }
            },
            {
              path: "role-action/:id?",
              name: "roleAction",
              component: () => import("./views/roleAction.vue"),
              meta: {
                breadcrumb: [
                  {
                    title: "breadcrumb.publications",
                    url: { name: "publications", params: { type: "paper" } }
                  },
                  { title: "breadcrumb.clientsList", url: { name: "clients" } },
                  { title: "breadcrumb.roles", url: { name: "roles" } },
                  { title: "breadcrumb.roleAction", active: true }
                ],
                pageTitle: "breadcrumb.roleAction"
              }
            }
          ]
        },
        {
          path: "profile",
          meta: {
            authRequired: true,
            isCustomer: true
          },
          component: () => import("./layouts/profile/"),
          children: [
            {
              path: "",
              name: "profile",
              component: () => import("./views/pages/profile.vue"),
              meta: {
                profileBreadcrumb: [
                  {
                    title: "breadcrumb.publications",
                    url: { name: "publications", params: { type: "paper" } }
                  },
                  { title: "breadcrumb.personalInformation", active: true }
                ],
                profilePageTitle: "breadcrumb.accountSettings"
              }
            },
            {
              path: "addresses",
              name: "addresses",
              component: () => import("./views/pages/addresses.vue"),
              meta: {
                profileBreadcrumb: [
                  {
                    title: "breadcrumb.publications",
                    url: { name: "publications", params: { type: "paper" } }
                  },
                  { title: "breadcrumb.profile", url: { name: "profile" } },
                  { title: "breadcrumb.addresses", active: true }
                ],
                profilePageTitle: "breadcrumb.addresses"
              }
            },
            {
              path: "end-user-addresses",
              name: "endUserAddresses",
              component: () => import("./views/pages/endUserAddresses.vue"),
              meta: {
                profileBreadcrumb: [
                  {
                    title: "breadcrumb.publications",
                    url: { name: "publications", params: { type: "paper" } }
                  },
                  { title: "breadcrumb.profile", url: { name: "profile" } },
                  { title: "breadcrumb.addresses", active: true }
                ],
                profilePageTitle: "breadcrumb.endUserAddresses"
              }
            },
            {
              path: "ip-and-emails",
              name: "ipAndEmails",
              component: () => import("./views/pages/emails.vue"),
              meta: {
                profileBreadcrumb: [
                  {
                    title: "breadcrumb.publications",
                    url: { name: "publications", params: { type: "paper" } }
                  },
                  { title: "breadcrumb.profile", url: { name: "profile" } },
                  { title: "breadcrumb.ipEmails", active: true }
                ],
                profilePageTitle: "breadcrumb.ipEmails"
              }
            },
            {
              path: "invoice-information",
              name: "invoiceInformation",
              component: () => import("./views/pages/invoiceInformation.vue"),
              meta: {
                profileBreadcrumb: [
                  {
                    title: "breadcrumb.publications",
                    url: { name: "publications", params: { type: "paper" } }
                  },
                  { title: "breadcrumb.profile", url: { name: "profile" } },
                  { title: "breadcrumb.invoiceInformation", active: true }
                ],
                profilePageTitle: "breadcrumb.invoiceInformation"
              }
            },
            {
              path: "security-information",
              name: "securityInformation",
              component: () => import("./views/pages/securityInformation.vue"),
              meta: {
                profileBreadcrumb: [
                  {
                    title: "breadcrumb.publications",
                    url: { name: "publications", params: { type: "paper" } }
                  },
                  { title: "breadcrumb.profile", url: { name: "profile" } },
                  { title: "breadcrumb.securityInformation", active: true }
                ],
                profilePageTitle: "breadcrumb.securityInformation"
              }
            }
          ]
        }
      ]
    },
    {
      // =============================================================================
      // MAIN LAYOUT ROUTES
      // =============================================================================
      path: "/publisher",
      meta: {
        authRequired: true,
        isPublisher: true
      },
      component: () => import("./layouts/main/Main.vue"),
      children: [
        // =============================================================================
        // Theme Routes
        // =============================================================================
        {
          path: "",
          name: "publisherHomePage",
          component: () => import("./views/publisherHomePage.vue"),
          meta: {
            breadcrumb: [
              {
                title: "breadcrumb.publications",
                url: {
                  name: "publicationsPublisher",
                  params: { type: "paper" }
                }
              }
            ],
            pageTitle: "breadcrumb.dashboard"
          }
        },
        {
          path: "publications",
          redirect: { name: "publicationsPublisher", params: { type: "paper" } }
        },
        {
          path: "publications/:type",
          name: "publicationsPublisher",
          component: () => import("./views/eCommerce/ECommerceShop.vue"),
          meta: {
            breadcrumb: [
              {
                title: "breadcrumb.publications",
                url: {
                  name: "publicationsPublisher",
                  params: { type: "paper" }
                }
              }
            ],
            pageTitle: "breadcrumb.publications"
          }
        },
        {
          path: "publications/:type/:by/:item",
          name: "publicationsPublisherBy",
          component: () => import("./views/eCommerce/ECommerceShop.vue"),
          meta: {
            breadcrumb: [
              {
                title: "breadcrumb.publications",
                url: {
                  name: "publicationsPublisher",
                  params: { type: "paper" }
                }
              },
              { title: "breadcrumb.publications", active: true }
            ],
            pageTitle: "breadcrumb.publications"
          }
        },
        {
          path: "publication/:id",
          name: "publicationPublisher",
          component: () => import("./views/eCommerce/publisherItemDetail.vue"),
          meta: {
            breadcrumb: [
              {
                title: "breadcrumb.publications",
                url: {
                  name: "publicationsPublisher",
                  params: { type: "paper" }
                }
              },
              {
                title: "breadcrumb.publications",
                url: {
                  name: "publicationsPublisher",
                  params: { type: "paper" }
                }
              },
              { title: "breadcrumb.publicationDetail", active: true }
            ],
            pageTitle: "breadcrumb.detail"
          }
        },
        {
          // path: "orders/:type",
          path: "orders",
          name: "publicationOrders",
          component: () => import("./views/orders/invoicesList.vue"),
          meta: {
            breadcrumb: [
              {
                title: "breadcrumb.publications",
                url: {
                  name: "publicationsPublisher",
                  params: { type: "paper" }
                }
              },
              { title: "breadcrumb.ordersList", active: true }
            ],
            pageTitle: "breadcrumb.ordersList"
          }
        },
        {
          path: "order/:id",
          name: "publisherOrderDetail",
          component: () =>
            import(
              "./views/orders/publisherInvoiceDetails/publisherInvoiceDetails.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "breadcrumb.publications",
                url: {
                  name: "publicationsPublisher",
                  params: { type: "paper" }
                }
              },
              {
                title: "breadcrumb.ordersList",
                url: { name: "publicationOrders", params: { type: "new" } }
              },
              { title: "breadcrumb.orderDetails", active: true }
            ],
            pageTitle: "breadcrumb.orderDetails"
          }
        },
        {
          path: "listPublications/:type",
          name: "listPublications",
          component: () =>
            import(
              "./views/orders/publisherListPublications/listPublications.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "breadcrumb.publications",
                url: {
                  name: "publicationsPublisher",
                  params: { type: "paper" }
                }
              },
              { title: "breadcrumb.listPublications", active: true }
            ],
            pageTitle: "breadcrumb.listPublications"
          }
        },
        // {
        //   path: "claims",
        //   name: "claimsPublisher",
        //   component: () => import("./views/claims/claimsList/claimsList.vue"),
        //   meta: {
        //     breadcrumb: [
        //       {
        //         title: "breadcrumb.publications",
        //         url: {
        //           name: "publicationsPublisher",
        //           params: { type: "paper" }
        //         }
        //       },
        //       { title: "breadcrumb.claims", active: true }
        //     ],
        //     pageTitle: "breadcrumb.claims"
        //   }
        // },
        {
          path: "claim/:id",
          name: "claimPublisherDetails",
          component: () => import("./views/claims/claimDetail/Chat.vue"),
          meta: {
            breadcrumb: [
              {
                title: "breadcrumb.publications",
                url: {
                  name: "publicationsPublisher",
                  params: { type: "paper" }
                }
              },
              { title: "breadcrumb.claims", url: { name: "claims" } },
              { title: "breadcrumb.claimDetails", active: true }
            ],
            pageTitle: "breadcrumb.claimDetails"
          }
        },
        {
          path: "reports",
          name: "publisherReports",
          component: () => import("./views/publisherReport.vue"),
          meta: {
            breadcrumb: [
              {
                title: "breadcrumb.publications",
                url: {
                  name: "publicationsPublisher",
                  params: { type: "paper" }
                }
              },
              { title: "breadcrumb.report", active: true }
            ],
            pageTitle: "breadcrumb.report"
          }
        },
        {
          path: "profile",
          name: "publisherProfile",
          component: () => import("./views/pages/publisherProfile.vue"),
          meta: {
            breadcrumb: [
              {
                title: "breadcrumb.publications",
                url: { name: "publications", params: { type: "paper" } }
              },
              { title: "breadcrumb.profile", active: true }
            ],
            pageTitle: "breadcrumb.profile"
          }
        },
        {
          path: "/ticket-history/:id/:publication",
          name: "ticketHistory",
          meta: {
            authRequired: true,
            isPublisher: true
          },
          component: () => import("./views/pages/ticketHistory.vue")
        },
        {
          path: "print-ticket",
          name: "printTicket",
          component: () => import("./views/pages/publisherPrintTicket.vue"),
          meta: {
            breadcrumb: [
              {
                title: "breadcrumb.publications",
                url: { name: "publications", params: { type: "paper" } }
              },
              { title: "breadcrumb.printTicket", active: true }
            ],
            pageTitle: "breadcrumb.printTicket"
          }
        }
      ]
    },
    {
      path: "/renewOrder/:token",
      name: "renewOrder",
      component: () => import("./views/orders/renewOrders.vue")
    },
    {
      path: "/change_address/:token",
      component: () => import("./views/orders/loginChangeAddress.vue")
    },
    {
      path: "/orders",
      redirect: { name: "invoices" }
    },
    {
      path: "/claim/:token",
      component: () => import("./views/claims/claimDetail/handleLogin.vue")
    },
    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: "",
      meta: {
        authRequired: false
      },
      component: () => import("@/layouts/full-page/FullPage.vue"),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: "/",
          redirect: { name: "login", params: { userType: "customer" } }
        },
        {
          path: "/:userType/login",
          name: "login",
          component: () => import("@/views/pages/Login.vue")
        },
        {
          path: "/register",
          name: "register",
          meta: {
            isCustomer: true
          },
          component: () => import("@/views/pages/register.vue")
        },
        {
          path: "/forget",
          name: "forget",
          component: () => import("@/views/pages/forget.vue")
        },
        {
          path: "/reset",
          name: "reset",
          component: () => import("@/views/pages/reset.vue")
        },
        {
          path: "/activate/",
          name: "activate",
          meta: {
            isCustomer: true
          },
          component: () => import("@/views/pages/activate.vue")
        },
        {
          path: "404",
          name: "error",
          component: () => import("@/views/pages/Error404.vue")
        }
      ]
    },
    {
      path: "/logout",
      name: "logout",
      meta: {
        authRequired: true,
        isUser: true
      },
      component: () => import("@/views/pages/logout.vue")
    },
    {
      path: "/ticket",
      name: "ticket",
      meta: {
        authRequired: true,
        isPublisher: true
      },
      component: () => import("./views/pages/ticket.vue")
    },

    // Redirect to 404 page, if no match found
    {
      path: "/:pathMatch(.*)*",
      redirect: { name: "error" }
    }
  ]
});

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById("loading-bg");
  if (appLoading) {
    appLoading.style.display = "none";
  }
});

router.beforeEach((to, from, next) => {
  const storedUser = localStorage.getItem("userInfo");
  const userType = storedUser
    ? JSON.parse(storedUser).data.user.type
    : null;
  const redirect = resolveRouteAccess(
    to.matched.map((record) => record.meta),
    userType,
    to.fullPath
  );
  redirect ? next(redirect) : next();
});

export default router;
